/**
 * Create a dish (Organizers only)
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useCreateDish } from '@/hooks/useDishes';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import { DishForm } from '@/components/dishes/DishForm';
import { useToast } from '@/components/ui/ToastProvider';
import type { CreateDishRequest } from '@/types/api';

export const Route = createFileRoute('/dishes/new')({
  beforeLoad: requireOrganizer,
  component: NewDishComponent,
});

function NewDishComponent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const createDish = useCreateDish();

  const handleSubmit = async (data: CreateDishRequest) => {
    try {
      await createDish.mutateAsync(data);
      toast.success(t('dishes.created'));
      navigate({ to: '/dishes' });
    } catch {
      toast.error(t('dishes.errorSaving'));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate({ to: '/dishes' })}
          className="mb-6 text-sm font-medium text-indigo-600 hover:text-indigo-800"
        >
          ← {t('dishes.backToList')}
        </button>
        <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {t('dishes.create')}
        </h1>
        <DishForm
          submitting={createDish.isPending}
          onSubmit={handleSubmit}
          onCancel={() => navigate({ to: '/dishes' })}
          submitLabel={t('dishes.create')}
        />
      </div>
    </div>
  );
}
